/**
 * Mapbox
 * 
 * @Author: Focci
 * @Date: 2023-06-16 16:33:42
 * @Last Modified by: Focci
 * @Last Modified time: 2023-06-16 16:33:42
 */

'use client'

import { featureCollection, envelope, point } from '@turf/turf'
import { createContext, forwardRef, useContext, useImperativeHandle, useRef } from 'react'
import { isFunction, flattenDeep, map, debounce } from 'lodash-es'
import Boundary from './Boundary'
import PolyLine from './PolyLine'
import useMapboxState from './useMapboxState'
import MapboxBase from './Mapbox'
import Layer from './layer'
import Marker from './marker'
import Popup from './popup'
import SchoolInfo from './SchoolInfo'
import StreetInfo from './StreetInfo'
import Events from './Events'

const MapboxState = createContext(null)

function addSetter(context) {
  const tmp = context

  tmp.valueSetter = (name, value) => {
    const setterName = `set${name.slice(0, 1).toUpperCase()}${name.slice(1)}`
    isFunction(tmp[setterName]) && tmp[setterName](value)
  }

  return tmp
}

/**
 * fitBounds
 * 
 * coords 格式为 [[{lng, lat}, {lng, lat}...]] or [[[lng, lat], [lng, lat]...]]
 * 
 * conversion 是否需要将 coords转换成 [[lng, lat]]形式
 */
function fitBounds(mapbox, coords, padding = 20, conversion = true) {
  try {
    const boundaries = conversion ? flattenDeep(coords) : coords
    const points = map(boundaries, (p) => point(conversion ? [p.lng, p.lat] : p))
    const fc = featureCollection(points)
    const envel = envelope(fc)
    const [a, b, c, d] = envel.bbox

    mapbox.fitBounds([[a, b], [c, d]], { padding, animate: false })
  } catch(err) {
    // 
  }
}

const fitBoundsDelay = debounce(fitBounds, 1000)

function Mapbox({
  children,
  events = {}, // 添加事件绑定, 见 Events.js
  ...props
}, ref) {
  const mapboxRef = useRef()
  const mps = useMapboxState()

  useImperativeHandle(ref, () => ({
    getMapbox: () => mapboxRef.current,
    getState: () => mps,
  }))

  return (
    <MapboxState.Provider value={mps}>
      <MapboxBase ref={mapboxRef} {...props}>
        {children}
        <Events events={events} />
      </MapboxBase>
    </MapboxState.Provider>
  )
}

export function MapboxProvider({ children }) {
  const mps = useMapboxState()

  return (
    <MapboxState.Provider value={mps}>
      {children}
    </MapboxState.Provider>
  )
}

export function MapboxWrapper({
  children,
  events = {}, // 添加事件绑定, 见 Events.js
  ...props
}) {
  return (
    <MapboxBase {...props}>
      {children}
      <Events events={events} />
    </MapboxBase>
  )
}

function useMapState() {
  return addSetter(useContext(MapboxState))
}

export default forwardRef(Mapbox)
export {
  Boundary,
  PolyLine,
  Layer,
  Marker,
  Popup,
  SchoolInfo,
  StreetInfo,
  useMapState,
  fitBounds,
  fitBoundsDelay
}
